/**
 * autoplay plugin
 *
 * Three responsibilities, all gated on the section's `data-autoplay="true"`:
 *
 *   1. Standard autoplay options — for sections WITHOUT `data-custom-autoplay`,
 *      translate data-attrs into Swiper's built-in `options.autoplay`:
 *
 *        data-autoplay-delay                 (ms; default 3000)
 *        data-autoplay-disable-on-interaction (default true)
 *        data-autoplay-pause-on-mouse-enter   (default false)
 *
 *      Nested carousels (`data-nested-carousel="true"`) use `data-speed` as
 *      the delay (default 5000ms) — faithful port of the legacy shorter-config
 *      shape, where decorative inner carousels reuse the same value to drive
 *      both transition speed and autoplay delay.
 *
 *   2. "Custom" autoplay (`data-custom-autoplay="true"`) — a unified setInterval
 *      that drives the carousel itself instead of relying on Swiper's built-in
 *      autoplay. The interval auto-adapts to viewport per tick:
 *
 *        - When every slide fits in the viewport (`totalSlides <= slidesPerView`,
 *          typical desktop): rotate the `swiper-slide-active/-prev/-next`
 *          classes and push synced followers via `slideTo`. The parent itself
 *          can't translate, so this is purely a class swap.
 *
 *        - When slides don't fit (typical mobile): call `swiper.slideTo(next)`
 *          on the parent. Swiper handles translation; `slideChange` fires;
 *          synced-swipers' own listener propagates to followers.
 *
 *      Because `swiper.params.slidesPerView` is breakpoint-aware (Swiper updates
 *      it automatically when crossing `breakpoints`), the same interval handles
 *      both viewports — no resize handler, no destroy/rebuild on viewport
 *      crossing. The previous design ran the desktop class-rotation timer AND
 *      Swiper's built-in autoplay simultaneously, which fought for ownership
 *      when crossing 990px and broke the carousel; this unified path eliminates
 *      that conflict.
 *
 *      For `data-custom-autoplay="true"` sections, we DO NOT set Swiper's
 *      built-in `options.autoplay` — our setInterval is the single source of
 *      truth. Used by `v3-outfits-carousel.liquid`.
 *
 *   3. Section-level play/pause controls — when a section contains a
 *      `.swiper-controls` element with a play and pause button, the buttons
 *      get wired to start/stop autoplay on every swiper in that section.
 *      Buttons are matched by ID prefix (`play-button*`/`pause-button*`).
 *      No effect on custom-autoplay sections (which don't render controls).
 *
 * Interaction model for custom autoplay:
 * Hover on a card is a "preview" gesture — pause the timer, seek to that
 * card; on mouseleave, resume. Hover is NOT counted as user interaction in
 * the disable-on-interaction sense.
 *
 * Manual nav (clicking a nav arrow, swiping the carousel, clicking a
 * pagination bullet) IS counted as user interaction:
 *   - `disable-on-interaction=true` (default): clear interval permanently,
 *     detach hover handlers so mouseleave can't restart it.
 *   - `disable-on-interaction=false`: clear and restart the interval, so
 *     autoplay resumes from the new activeIndex.
 *
 * Coordination with synced-swipers:
 * When the parent calls `slideTo` (mobile path), `slideChange` fires and
 * synced-swipers propagates. When the parent rotates classes (desktop path),
 * `slideChange` does NOT fire — so this plugin pushes followers directly via
 * `ctx.registry.getSyncGroup`. Both paths leave followers in sync.
 *
 * Viewport-cross rebuild:
 * Swiper carries `activeIndex` and wrapper translate across breakpoint
 * crossings, and not every param explicitly listed in `data-breakpoints`
 * round-trips cleanly back to its base value. The visible result on a
 * desktop layout returning from mobile is "centered on the active slide
 * instead of looking like initial load". To bypass that stickiness, the
 * custom-autoplay path watches `allSlidesFit(swiper)` on a debounced window
 * resize; when it flips (mobile→desktop or desktop→mobile), the plugin asks
 * the Initializer to destroy + re-init this section. The new instance starts
 * from `activeIndex=0` with a fresh translate, matching DOM-load layout.
 */

import { allSlidesFit, seekTo } from '../utils/seek.js';

const intervalsBySwiper = new WeakMap();
const slideListenersBySwiper = new WeakMap();
const interactionListenersBySwiper = new WeakMap();

/** Per-controls-element cache of the `updateButtonStates(isPlaying)` closure
 *  so that re-init cycles (resize tear-down + rebuild) can drive the same
 *  buttons without re-binding click handlers each time. */
const updateButtonStatesByControls = new WeakMap();

// ---------- attribute helpers ----------

function isAutoplayEnabled(section) {
  return section?.dataset?.autoplay === 'true';
}

function isCustomAutoplay(section) {
  return section?.dataset?.customAutoplay === 'true';
}

function isNestedCarousel(section) {
  return section?.dataset?.nestedCarousel === 'true';
}

function isDisableOnInteraction(section) {
  return section?.dataset?.autoplayDisableOnInteraction !== 'false';
}

function isPauseOnMouseEnter(section) {
  return section?.dataset?.autoplayPauseOnMouseEnter === 'true';
}

function readDelay(section) {
  const data = section.dataset;
  if (isNestedCarousel(section)) {
    return data.speed ? parseInt(data.speed, 10) : 5000;
  }
  return data.autoplayDelay ? parseInt(data.autoplayDelay, 10) : 3000;
}

// ---------- per-swiper cleanup ----------

function clearAutoplayInterval(swiper) {
  const id = intervalsBySwiper.get(swiper);
  if (id != null) {
    clearInterval(id);
    intervalsBySwiper.delete(swiper);
  }
}

function detachSlideListeners(swiper) {
  const listeners = slideListenersBySwiper.get(swiper);
  if (!listeners) return;
  for (const { slide, enter, leave } of listeners) {
    slide.removeEventListener('mouseenter', enter);
    slide.removeEventListener('mouseleave', leave);
  }
  slideListenersBySwiper.delete(swiper);
}

function detachInteractionListeners(swiper) {
  const cleanups = interactionListenersBySwiper.get(swiper);
  if (!cleanups) return;
  for (const cleanup of cleanups) {
    try {
      cleanup();
    } catch (err) {
      console.warn('autoplay: interaction listener cleanup failed', err);
    }
  }
  interactionListenersBySwiper.delete(swiper);
}

// ---------- custom autoplay (unified across viewports) ----------
//
// The fit-aware "advance the carousel to an index" logic (class rotation when
// all slides fit, `slideTo` when they don't) lives in `utils/seek.js` so the
// autoplay tick, the hover seek, and the card-flip plugin all move the
// carousel the same way and drive the same synced followers.

function setupCustomAutoplay(swiper, ctx, section, delay) {
  const { registry, initializer } = ctx;
  const syncId = section.dataset?.syncId || null;
  const disableOnInteraction = isDisableOnInteraction(section);
  const pauseOnHover = isPauseOnMouseEnter(section);

  // When `disableOnInteraction=true` and the user manually navigates, we set
  // this flag so mouseleave (the per-slide hover handler) can't restart the
  // interval. The flag also short-circuits any pending `startInterval` calls.
  let interactionDisabled = false;

  // The all-fit (class-rotation) path never calls `slideTo` on the parent, so
  // `swiper.activeIndex` stays put — track our own index for that path or the
  // tick would compute the same "next" forever and the carousel would freeze
  // after a single advance. The translate path still reads `swiper.activeIndex`
  // so a manual swipe (when disable-on-interaction=false restarts the timer)
  // resumes from where the user left it.
  let currentIndex = swiper.activeIndex || 0;

  const advanceTo = (newIndex) => {
    if (swiper.destroyed) return;
    currentIndex = newIndex;
    seekTo(swiper, registry, syncId, newIndex);
  };

  const computeNextIndex = () => {
    const total = swiper.slides.length;
    if (total === 0) return 0;
    const base = allSlidesFit(swiper) ? currentIndex : swiper.activeIndex;
    return base >= total - 1 ? 0 : base + 1;
  };

  const startInterval = () => {
    clearAutoplayInterval(swiper);
    if (interactionDisabled) return;
    const id = setInterval(() => {
      if (swiper.destroyed) {
        clearAutoplayInterval(swiper);
        return;
      }
      if (pauseOnHover && swiper.el.matches(':hover')) return;
      advanceTo(computeNextIndex());
    }, delay);
    intervalsBySwiper.set(swiper, id);
  };

  // Per-slide hover: pause + seek on mouseenter, restart on mouseleave.
  // Hover is intentionally NOT treated as "user interaction" for the
  // disable-on-interaction contract — it's a preview gesture.
  const slideListeners = [];
  swiper.slides.forEach((slide, index) => {
    const enter = () => {
      clearAutoplayInterval(swiper);
      advanceTo(index);
    };
    const leave = () => {
      if (interactionDisabled) return;
      startInterval();
    };
    slide.addEventListener('mouseenter', enter);
    slide.addEventListener('mouseleave', leave);
    slideListeners.push({ slide, enter, leave });
  });
  slideListenersBySwiper.set(swiper, slideListeners);

  // Interaction detection — fires on swipe (touchEnd, also fires for
  // simulated mouse-drag), nav arrow click, and pagination bullet click.
  // Hover and our own internal slideTo calls do NOT trip these.
  const onUserInteraction = () => {
    if (disableOnInteraction) {
      interactionDisabled = true;
      clearAutoplayInterval(swiper);
      detachSlideListeners(swiper);
    } else {
      startInterval();
    }
  };

  const cleanups = [];

  // Swipe / drag detection. `touchEnd` fires for both touch and mouse drag
  // (Swiper emits it via simulateTouch).
  swiper.on('touchEnd', onUserInteraction);
  cleanups.push(() => {
    // Swiper.destroy() removes its own listeners, but if the autoplay state
    // is torn down before the swiper itself (e.g. plugin re-init), detach
    // explicitly to avoid double-firing.
    try {
      swiper.off('touchEnd', onUserInteraction);
    } catch {
      // swiper already destroyed; nothing to do.
    }
  });

  // Nav arrow clicks (custom selector, mirrors options-parser's lookup).
  const prevBtn = section.querySelector('[class*="__button--prev"]');
  const nextBtn = section.querySelector('[class*="__button--next"]');
  if (prevBtn) {
    prevBtn.addEventListener('click', onUserInteraction);
    cleanups.push(() => prevBtn.removeEventListener('click', onUserInteraction));
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', onUserInteraction);
    cleanups.push(() => nextBtn.removeEventListener('click', onUserInteraction));
  }

  // Pagination bullet clicks (delegated on the container — Swiper renders
  // bullets dynamically so we can't bind to them individually at init).
  const paginationEl = section.querySelector('[class*="__pagination"]');
  if (paginationEl) {
    paginationEl.addEventListener('click', onUserInteraction);
    cleanups.push(() => paginationEl.removeEventListener('click', onUserInteraction));
  }

  // A tap/click on a card (the card-flip gesture) is also user interaction,
  // but it needs no dedicated listener here: `touchEnd` above fires on every
  // tap and click (via simulateTouch), so it already covers card taps.

  // Viewport-cross rebuild. Swiper's breakpoint handler doesn't fully reset
  // wrapper translate / activeIndex state across crossings, so a section that
  // lays out as "all cards visible" on desktop and "translating carousel" on
  // mobile ends up visually broken when returning to desktop. Watch the
  // allSlidesFit predicate (same one the tick uses) via the initializer's
  // shared resize bus (one debounce timer for the whole page, fired AFTER
  // `_initOrDestroyAll` reconciles the registry); when it flips, ask the
  // Initializer to destroy + re-init the section so the new swiper instance
  // starts from initial-load state.
  if (
    initializer &&
    typeof initializer.rebuildSection === 'function' &&
    typeof initializer.onResize === 'function'
  ) {
    let wasFitting = allSlidesFit(swiper);
    const unsubscribe = initializer.onResize(() => {
      if (swiper.destroyed) return;
      const isFitting = allSlidesFit(swiper);
      if (isFitting === wasFitting) return;
      wasFitting = isFitting;
      try {
        initializer.rebuildSection(section);
      } catch (err) {
        console.warn('autoplay: rebuild on viewport crossing failed', err);
      }
    });
    cleanups.push(unsubscribe);
  }

  interactionListenersBySwiper.set(swiper, cleanups);

  startInterval();
}

// ---------- play/pause controls ----------

/**
 * Bind click handlers on the play/pause buttons exactly once per controls
 * element. Across re-init cycles (resize-driven destroy + rebuild) the
 * handlers stay bound and re-read the LATEST swiper instances from the
 * registry on each click, so they automatically work against the new
 * Swipers without listener stacking.
 *
 * Returns the `updateButtonStates(isPlaying)` closure so per-swiper
 * `autoplayStart` / `autoplayStop` listeners can drive the same UI state.
 * Returns `null` if the controls element doesn't have the expected button
 * shape (the section may import the controls CSS without rendering them).
 */
function ensureClickHandlersBound(section, registry) {
  const controls = section.querySelector('.swiper-controls');
  if (!controls) return null;

  const cached = updateButtonStatesByControls.get(controls);
  if (cached) return cached;

  // Prefix match handles both `#play-button` (literal) and
  // `#play-button-${section.id}` (suffixed, as in v3-simple-carousel.liquid).
  const playBtn = controls.querySelector('[id^="play-button"]');
  const pauseBtn = controls.querySelector('[id^="pause-button"]');
  if (!playBtn || !pauseBtn) return null;

  // Intentional behavior: a user clicking the play/pause buttons is NOT
  // counted as `disable-on-interaction`. The buttons are explicit autoplay
  // controls — pressing pause should pause, not also "disable autoplay
  // forever because you interacted with the carousel". This mirrors how a
  // video player's pause button doesn't disable future plays.
  //
  // The `disable-on-interaction` flag covers content interactions (swipe,
  // nav arrow click, pagination bullet click) where Swiper's built-in
  // autoplay treats user agency as "they're driving now, stop driving for
  // them." Section-level controls are an explicit user request to manage
  // autoplay state, not a sign of "user has taken over." If you ever want
  // to treat controls clicks as interactions, do so via a separate flag —
  // don't conflate the two.

  const updateButtonStates = (isPlaying) => {
    playBtn.setAttribute('aria-pressed', String(!isPlaying));
    pauseBtn.setAttribute('aria-pressed', String(isPlaying));
    playBtn.style.display = isPlaying ? 'none' : 'block';
    pauseBtn.style.display = isPlaying ? 'block' : 'none';
  };

  playBtn.addEventListener('click', () => {
    const swipers = registry.getSectionSwipers(section) || [];
    swipers.forEach((s) => {
      if (s.autoplay) s.autoplay.start();
    });
    updateButtonStates(true);
  });

  pauseBtn.addEventListener('click', () => {
    const swipers = registry.getSectionSwipers(section) || [];
    swipers.forEach((s) => {
      if (s.autoplay) s.autoplay.stop();
    });
    updateButtonStates(false);
  });

  updateButtonStatesByControls.set(controls, updateButtonStates);
  return updateButtonStates;
}

// ---------- plugin contract ----------

export const autoplay = {
  name: 'autoplay',

  matches(_el, section) {
    return isAutoplayEnabled(section);
  },

  parseOptions(_el, section, options) {
    // Custom-autoplay sections drive everything from a single setInterval
    // (see setupCustomAutoplay). Wiring Swiper's built-in autoplay alongside
    // would create dual timers that fight for ownership across viewports.
    if (isCustomAutoplay(section)) {
      return options;
    }

    const data = section.dataset;
    const delay = readDelay(section);
    options.autoplay = {
      delay,
      disableOnInteraction: data.autoplayDisableOnInteraction !== 'false',
      pauseOnMouseEnter: data.autoplayPauseOnMouseEnter === 'true',
    };
    return options;
  },

  afterInit(swiper, ctx) {
    const section = ctx.parentSwiperSection;
    if (!isCustomAutoplay(section)) return;
    if (isNestedCarousel(section)) return;

    // Off-screen slides get aria-hidden once when customAutoplay is set.
    // Swiper itself manages aria-hidden during translate; the manual setup
    // matters mainly for the all-fit / class-rotation path where the parent
    // never translates.
    swiper.slides.forEach((slide, index) => {
      if (typeof swiper.params.slidesPerView === 'number' && index >= swiper.params.slidesPerView) {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    const delay = readDelay(section);
    setupCustomAutoplay(swiper, ctx, section, delay);
  },

  afterAllInit(swiper, ctx) {
    const outerSection = ctx.section;
    const updateButtonStates = ensureClickHandlersBound(outerSection, ctx.registry);
    if (!updateButtonStates) return;

    // Per-swiper UI state listeners. These die with the swiper instance, so
    // they get re-attached on every init cycle (correct, idempotent).
    if (swiper.autoplay) {
      swiper.on('autoplayStart', () => updateButtonStates(true));
      swiper.on('autoplayStop', () => updateButtonStates(false));
    }
    updateButtonStates(!!swiper.autoplay?.running);
  },

  onDestroy(swiper) {
    clearAutoplayInterval(swiper);
    detachSlideListeners(swiper);
    detachInteractionListeners(swiper);
    // No controls cleanup needed: click handlers stay bound across re-init
    // cycles via `updateButtonStatesByControls` and re-read the registry
    // each click. autoplayStart/Stop listeners live on the Swiper instance
    // and get auto-detached by `swiper.destroy()`.
  },
};

export default autoplay;
